/**
 * Edit Company File — Cloud GM Company Agent (Feb 2026 / iter-182).
 * Loads the existing company, lets admin edit all fields, saves via PATCH.
 * No license file changes here — renewal has its own dedicated flow.
 */
import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { motion } from 'framer-motion';
import { Loader2, Save, Trash2, Plus, FileText, UserPlus, Search, X } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { useLanguage } from '../context/LanguageContext';
import { getAuthHeaders } from '../utils/authHeaders';
import { isValidEmail, isValidPhone } from '../utils/validators';
import { AgentPageShell } from './agent/AgentPageShell';
import { LinkedAgentTaskPicker, taskToSnapshot } from './agent/LinkedAgentTaskPicker';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AgentCompanyEditPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { language } = useLanguage();
  const isAr = language === 'ar';
  const [form, setForm] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  // agent search state
  const [agentMode, setAgentMode] = useState('none');
  const [agentQ, setAgentQ] = useState('');
  const [agentResults, setAgentResults] = useState([]);

  const setField = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  useEffect(() => {
    (async () => {
      try {
        const res = await axios.get(`${API}/agent/companies/${id}`, { headers: getAuthHeaders() });
        const c = res.data?.data;
        setForm({
          company_name_ar: c.company_name_ar || '',
          company_name_en: c.company_name_en || '',
          license_number: c.license_number || '',
          unified_license_number: c.unified_license_number || '',
          issuing_authority: c.issuing_authority || '',
          license_type: c.license_type || '',
          legal_form: c.legal_form || '',
          issue_date: c.issue_date || '',
          expiry_date: c.expiry_date || '',
          capital: c.capital || '',
          activities: Array.isArray(c.activities) ? c.activities : [],
          partners: Array.isArray(c.partners) ? c.partners : [],
          address: c.address || '',
          po_box: c.po_box || '',
          phone: c.phone || '',
          email: c.email || '',
          notes: c.notes || '',
          agent_id: c.agent_id || null,
          agent_name: c.agent_name || '',
          agent_email: c.agent_email || '',
          agent_phone: c.agent_phone || '',
          // Feb 2026 iter-200 — linked agent task (replaces linked tax invoice)
          linked_agent_task_id: c.linked_agent_task_id || null,
          linked_agent_task_ref: c.linked_agent_task_ref || '',
          linked_agent_task_snapshot: c.linked_agent_task_snapshot || null,
        });
        setAgentMode(c.agent_id ? 'registered' : ((c.agent_name || c.agent_email || c.agent_phone) ? 'new' : 'none'));
      } catch (err) {
        toast.error(err.response?.data?.detail || err.message);
      } finally {
        setLoading(false);
      }
    })();
  }, [id]);

  // debounced agent search
  useEffect(() => {
    if (agentMode !== 'registered' || !agentQ.trim() || agentQ.trim().length < 2) {
      setAgentResults([]);
      return;
    }
    const t = setTimeout(async () => {
      try {
        const r = await axios.get(`${API}/admin/assigned-tasks/targets/search`, {
          headers: getAuthHeaders(),
          params: { type: 'agent', q: agentQ.trim() },
        });
        setAgentResults(r.data?.data || []);
      } catch { setAgentResults([]); }
    }, 300);
    return () => clearTimeout(t);
  }, [agentQ, agentMode]);

  const pickAgent = (u) => {
    setForm((f) => ({
      ...f,
      agent_id: u.id,
      agent_name: u.name || '',
      agent_email: u.email || '',
      agent_phone: u.phone || '',
    }));
    setAgentQ(u.name || u.email || '');
    setAgentResults([]);
  };
  const clearAgent = () => {
    setForm((f) => ({
      ...f,
      agent_id: null, agent_name: '', agent_email: '', agent_phone: '',
      // Feb 2026 iter-200 — task link must follow the agent's lifecycle.
      linked_agent_task_id: null,
      linked_agent_task_ref: '',
      linked_agent_task_snapshot: null,
    }));
    setAgentQ(''); setAgentResults([]);
  };

  const handleLinkedTask = (task) => {
    if (!task) {
      setForm((f) => ({
        ...f,
        linked_agent_task_id: null,
        linked_agent_task_ref: '',
        linked_agent_task_snapshot: null,
      }));
      return;
    }
    setForm((f) => ({
      ...f,
      linked_agent_task_id: task.id,
      linked_agent_task_ref: task.request_no_display || '',
      linked_agent_task_snapshot: taskToSnapshot(task),
    }));
  };

  const addPartner = () => setForm((f) => ({ ...f, partners: [...f.partners, { name: '', share: '' }] }));
  const setPartner = (idx, key, val) => setForm((f) => ({
    ...f, partners: f.partners.map((p, i) => (i === idx ? { ...p, [key]: val } : p)),
  }));
  const removePartner = (idx) => setForm((f) => ({ ...f, partners: f.partners.filter((_, i) => i !== idx) }));

  const addActivity = () => setForm((f) => ({ ...f, activities: [...f.activities, ''] }));
  const setActivity = (idx, val) => setForm((f) => ({ ...f, activities: f.activities.map((a, i) => (i === idx ? val : a)) }));
  const removeActivity = (idx) => setForm((f) => ({ ...f, activities: f.activities.filter((_, i) => i !== idx) }));

  const handleSave = async () => {
    if (!form.company_name_ar && !form.company_name_en) {
      toast.error(isAr ? 'اسم الشركة إلزامي' : 'Company name is required');
      return;
    }
    // Feb 2026 (iter-184) — validate optional-but-formatted fields.
    if (form.phone && !isValidPhone(form.phone)) {
      toast.error(isAr ? 'رقم هاتف الشركة غير صحيح' : 'Company phone number is not valid');
      return;
    }
    if (form.email && !isValidEmail(form.email)) {
      toast.error(isAr ? 'البريد الإلكتروني للشركة غير صحيح' : 'Company email is not valid');
      return;
    }
    if (form.agent_phone && !isValidPhone(form.agent_phone)) {
      toast.error(isAr ? 'رقم هاتف الوكيل غير صحيح' : 'Agent phone number is not valid');
      return;
    }
    if (form.agent_email && !isValidEmail(form.agent_email)) {
      toast.error(isAr ? 'بريد الوكيل الإلكتروني غير صحيح' : 'Agent email is not valid');
      return;
    }
    setSaving(true);
    try {
      // If agent mode is 'none', wipe agent fields + linked task
      const payload = { ...form };
      if (agentMode === 'none') {
        payload.agent_id = null; payload.agent_name = ''; payload.agent_email = ''; payload.agent_phone = '';
        payload.linked_agent_task_id = null;
        payload.linked_agent_task_ref = '';
        payload.linked_agent_task_snapshot = null;
      }
      await axios.patch(`${API}/agent/companies/${id}`, payload, { headers: getAuthHeaders() });
      toast.success(isAr ? 'تم حفظ التعديلات' : 'Changes saved');
      navigate(`/manage/cloudgm-agent/companies/${id}`);
    } catch (err) {
      toast.error(err.response?.data?.detail || err.message);
    } finally {
      setSaving(false);
    }
  };

  const breadcrumb = [
    { label_ar: 'الرئيسية', label_en: 'Home', path: '/dashboard' },
    { label_ar: 'Cloud GM Company Agent', label_en: 'Cloud GM Company Agent', path: '/manage/cloudgm-agent' },
    { label_ar: 'متابعة ملف الشركات', label_en: 'Track Company Files', path: '/manage/cloudgm-agent/companies' },
    { label_ar: 'تعديل', label_en: 'Edit', path: null },
  ];

  if (loading || !form) {
    return (
      <AgentPageShell breadcrumb={breadcrumb} onBack={() => navigate(`/manage/cloudgm-agent/companies/${id}`)}>
        <div className="text-center py-16 text-[#808080]">{isAr ? 'جاري التحميل…' : 'Loading…'}</div>
      </AgentPageShell>
    );
  }

  return (
    <AgentPageShell
      breadcrumb={breadcrumb}
      maxWidth="max-w-5xl"
      onBack={() => navigate(`/manage/cloudgm-agent/companies/${id}`)}
    >
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-2xl font-bold text-white" data-testid="agent-company-edit-title">
          {isAr ? 'تعديل ملف الشركة' : 'Edit Company File'}
        </h1>
      </div>

      {/* Agent picker */}
      <motion.section initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} className="bg-white/[0.02] border border-white/10 rounded-2xl p-6 mb-6">
        <div className="flex items-center gap-2 mb-3">
          <UserPlus className="h-5 w-5 text-cyan-400" />
          <h2 className="font-semibold text-white">{isAr ? 'وكيل الشركة' : 'Company Agent'}</h2>
        </div>
        <div className="flex flex-wrap gap-2 mb-4">
          {['none', 'registered', 'new'].map((mode) => (
            <button
              key={mode}
              onClick={() => { setAgentMode(mode); if (mode === 'none') clearAgent(); }}
              data-testid={`edit-agent-mode-${mode}`}
              className={`px-3 py-1.5 rounded-lg text-sm border transition-colors ${
                agentMode === mode
                  ? 'bg-cyan-500/20 border-cyan-500/40 text-cyan-300'
                  : 'bg-transparent border-white/10 text-[#808080] hover:bg-white/5'
              }`}
            >
              {mode === 'none' ? (isAr ? 'بدون وكيل' : 'No agent')
                : mode === 'registered' ? (isAr ? 'وكيل مسجّل' : 'Registered agent')
                : (isAr ? 'وكيل جديد (غير مسجّل)' : 'New agent (unregistered)')}
            </button>
          ))}
        </div>

        {agentMode === 'registered' && (
          <div className="relative">
            <div className="flex items-center gap-2 bg-[#050505] border border-white/10 rounded-lg px-3 py-2">
              <Search className="h-4 w-4 text-[#606060]" />
              <input
                value={agentQ}
                onChange={(e) => { setAgentQ(e.target.value); if (form.agent_id) setField('agent_id', null); }}
                placeholder={isAr ? 'ابحث…' : 'Search…'}
                className="flex-1 bg-transparent text-white text-sm outline-none placeholder:text-[#606060]"
              />
              {form.agent_id && <button onClick={clearAgent} className="text-red-400"><X className="h-4 w-4" /></button>}
            </div>
            {agentResults.length > 0 && !form.agent_id && (
              <div className="absolute z-20 top-full mt-1 left-0 right-0 bg-[#0a0a0a] border border-white/10 rounded-lg max-h-60 overflow-y-auto">
                {agentResults.map((u) => (
                  <button key={u.id} onClick={() => pickAgent(u)} className="w-full text-start px-3 py-2 hover:bg-white/5 border-b border-white/5 last:border-b-0">
                    <div className="text-sm text-white">{u.name || u.email}</div>
                    <div className="text-xs text-[#808080]">{u.email} {u.phone && `· ${u.phone}`}</div>
                  </button>
                ))}
              </div>
            )}
            {form.agent_id && (
              <p className="text-xs text-emerald-400 mt-2">
                {isAr ? 'محدَّد:' : 'Selected:'} {form.agent_name} {form.agent_email && `· ${form.agent_email}`}
              </p>
            )}
          </div>
        )}

        {agentMode === 'new' && (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <div><Label>{isAr ? 'اسم الوكيل' : 'Agent name'}</Label><Input value={form.agent_name} onChange={(e) => setField('agent_name', e.target.value)} /></div>
            <div><Label>{isAr ? 'البريد' : 'Email'}</Label><Input type="email" value={form.agent_email} onChange={(e) => setField('agent_email', e.target.value)} /></div>
            <div><Label>{isAr ? 'الهاتف' : 'Phone'}</Label><Input value={form.agent_phone} onChange={(e) => setField('agent_phone', e.target.value)} /></div>
          </div>
        )}
      </motion.section>

      {/* Linked agent task (iter-200) — visible only when an agent is present */}
      {agentMode !== 'none' && (
        <LinkedAgentTaskPicker
          agentEmail={form.agent_email}
          selectedId={form.linked_agent_task_id}
          selectedRef={form.linked_agent_task_ref}
          selectedSnapshot={form.linked_agent_task_snapshot}
          onSelect={handleLinkedTask}
        />
      )}

      <motion.section initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.05 }} className="bg-white/[0.02] border border-white/10 rounded-2xl p-6 mb-6 space-y-4">
        <div className="flex items-center gap-2 mb-2">
          <FileText className="h-5 w-5 text-cyan-400" />
          <h2 className="font-semibold text-white">{isAr ? 'بيانات الشركة' : 'Company details'}</h2>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div><Label>{isAr ? 'الاسم (عربي)' : 'Name (AR)'}</Label><Input value={form.company_name_ar} onChange={(e) => setField('company_name_ar', e.target.value)} data-testid="edit-name-ar" /></div>
          <div><Label>{isAr ? 'الاسم (إنجليزي)' : 'Name (EN)'}</Label><Input value={form.company_name_en} onChange={(e) => setField('company_name_en', e.target.value)} data-testid="edit-name-en" /></div>
          <div><Label>{isAr ? 'رقم الرخصة' : 'License #'}</Label><Input value={form.license_number} onChange={(e) => setField('license_number', e.target.value)} /></div>
          <div><Label>{isAr ? 'الرقم الموحد' : 'Unified #'}</Label><Input value={form.unified_license_number} onChange={(e) => setField('unified_license_number', e.target.value)} /></div>
          <div><Label>{isAr ? 'جهة الإصدار' : 'Authority'}</Label><Input value={form.issuing_authority} onChange={(e) => setField('issuing_authority', e.target.value)} /></div>
          <div><Label>{isAr ? 'نوع الرخصة' : 'License type'}</Label><Input value={form.license_type} onChange={(e) => setField('license_type', e.target.value)} /></div>
          <div><Label>{isAr ? 'الشكل القانوني' : 'Legal form'}</Label><Input value={form.legal_form} onChange={(e) => setField('legal_form', e.target.value)} /></div>
          <div><Label>{isAr ? 'رأس المال' : 'Capital'}</Label><Input value={form.capital} onChange={(e) => setField('capital', e.target.value)} /></div>
          <div><Label>{isAr ? 'تاريخ الإصدار' : 'Issue date'}</Label><Input type="date" value={form.issue_date} onChange={(e) => setField('issue_date', e.target.value)} /></div>
          <div><Label>{isAr ? 'تاريخ الانتهاء' : 'Expiry date'}</Label><Input type="date" value={form.expiry_date} onChange={(e) => setField('expiry_date', e.target.value)} /></div>
          <div><Label>{isAr ? 'ص.ب' : 'PO box'}</Label><Input value={form.po_box} onChange={(e) => setField('po_box', e.target.value)} /></div>
          <div>
            <Label>{isAr ? 'الهاتف' : 'Phone'} <span className="text-white/30 text-[10px]">{isAr ? '(اختياري)' : '(optional)'}</span></Label>
            <Input
              value={form.phone}
              onChange={(e) => setField('phone', e.target.value)}
              placeholder="+971 50 000 0000"
              className={form.phone && !isValidPhone(form.phone) ? 'border-red-500/60 focus-visible:ring-red-500' : ''}
              data-testid="edit-fld-phone"
            />
            {form.phone && !isValidPhone(form.phone) && (
              <p className="text-[11px] text-red-400 mt-1">{isAr ? 'رقم الهاتف غير صحيح' : 'Invalid phone number'}</p>
            )}
          </div>
          <div className="md:col-span-2"><Label>{isAr ? 'العنوان' : 'Address'}</Label><Input value={form.address} onChange={(e) => setField('address', e.target.value)} /></div>
          <div className="md:col-span-2">
            <Label>{isAr ? 'الإيميل' : 'Email'} <span className="text-white/30 text-[10px]">{isAr ? '(اختياري)' : '(optional)'}</span></Label>
            <Input
              type="email"
              value={form.email}
              onChange={(e) => setField('email', e.target.value)}
              placeholder="info@example.com"
              className={form.email && !isValidEmail(form.email) ? 'border-red-500/60 focus-visible:ring-red-500' : ''}
              data-testid="edit-fld-email"
            />
            {form.email && !isValidEmail(form.email) && (
              <p className="text-[11px] text-red-400 mt-1">{isAr ? 'البريد الإلكتروني غير صحيح' : 'Invalid email'}</p>
            )}
          </div>
        </div>

        {/* Activities */}
        <div className="border-t border-white/10 pt-4">
          <div className="flex items-center justify-between mb-3">
            <Label>{isAr ? 'الأنشطة' : 'Activities'}</Label>
            <Button size="sm" variant="outline" className="border-[#2a2a4e] rounded-lg" onClick={addActivity}>
              <Plus className="h-3.5 w-3.5 me-1" />{isAr ? 'إضافة' : 'Add'}
            </Button>
          </div>
          <div className="space-y-2">
            {form.activities.map((a, idx) => (
              <div key={idx} className="flex gap-2">
                <Input value={a} onChange={(e) => setActivity(idx, e.target.value)} />
                <Button variant="ghost" size="icon" onClick={() => removeActivity(idx)}><Trash2 className="h-4 w-4 text-red-400" /></Button>
              </div>
            ))}
          </div>
        </div>

        {/* Partners */}
        <div className="border-t border-white/10 pt-4">
          <div className="flex items-center justify-between mb-3">
            <Label>{isAr ? 'الشركاء' : 'Partners'}</Label>
            <Button size="sm" variant="outline" className="border-[#2a2a4e] rounded-lg" onClick={addPartner}>
              <Plus className="h-3.5 w-3.5 me-1" />{isAr ? 'إضافة' : 'Add'}
            </Button>
          </div>
          <div className="space-y-2">
            {form.partners.map((p, idx) => (
              <div key={idx} className="grid grid-cols-1 sm:grid-cols-[1fr,180px,44px] gap-2">
                <Input placeholder={isAr ? 'الاسم' : 'Name'} value={p.name || ''} onChange={(e) => setPartner(idx, 'name', e.target.value)} />
                <Input placeholder={isAr ? 'النسبة %' : 'Share %'} value={p.share || ''} onChange={(e) => setPartner(idx, 'share', e.target.value)} />
                <Button variant="ghost" size="icon" onClick={() => removePartner(idx)}><Trash2 className="h-4 w-4 text-red-400" /></Button>
              </div>
            ))}
          </div>
        </div>

        <div><Label>{isAr ? 'ملاحظات' : 'Notes'}</Label><Textarea rows={3} value={form.notes} onChange={(e) => setField('notes', e.target.value)} /></div>
      </motion.section>

      <div className="flex justify-end gap-3">
        <Button variant="outline" className="border-[#2a2a4e] rounded-xl text-white" onClick={() => navigate(`/manage/cloudgm-agent/companies/${id}`)}>
          {isAr ? 'إلغاء' : 'Cancel'}
        </Button>
        <Button onClick={handleSave} disabled={saving} className="bg-emerald-600 hover:bg-emerald-700 text-white rounded-xl" data-testid="edit-save-btn">
          {saving ? <Loader2 className="h-4 w-4 me-2 animate-spin" /> : <Save className="h-4 w-4 me-2" />}
          {isAr ? 'حفظ التعديلات' : 'Save changes'}
        </Button>
      </div>
    </AgentPageShell>
  );
};

export default AgentCompanyEditPage;
